import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useScreenSize, window } from "@swa-ui/browser";
import { swaDate } from "@swa-ui/date";
import { classNames } from "@swa-ui/string";

import { Caption } from "../Caption";
import { DateInput } from "../DateInput";
import { keyCodes } from "../defines/keyCodes";
import { FormattedDate } from "../FormattedDate";
import { Heading } from "../Heading";
import { inputPropTypes } from "../Input";
import { Link } from "../Link";
import { Calendar } from "./Calendar";
import { CalendarSecondary } from "./CalendarSecondary";
import styles from "./DateSelector.module.scss";

const FULL_DATE_FORMAT = "MM/DD/YYYY";
const MIN_WINDOW_WIDTH_FOR_HORIZONTAL_CALENDARS = 600;

/**
 * DateSelector uses FormattedDate and a flyout calendar to facilitate date entry. When DateSelector receives focus,
 * it will display a flyout with a calendar where the traveler can select a date, or the traveler can enter a date using
 * the keyboard using the FormattedDate component.
 */
export const DateSelector = React.forwardRef((props, ref) => {
  const {
    adjunctContent,
    className,
    closeContent,
    dateEnd,
    dateStart,
    daysOfWeekInitials,
    departText,
    disabled,
    error,
    firstBookableDate = swaDate().startOf("day").format("MM/DD/YYYY"),
    focusField,
    format = "MM/DD/YYYY",
    id,
    lastBookableDate,
    location = "below",
    name,
    numberCalendars,
    numberDates = 1,
    onChange,
    onClose,
    onSelectEnd,
    onSelectStart,
    onSetupEnd,
    openOnFocus,
    required,
    returnText,
    selectDateText,
    shouldLeaveOpen,
    simulateNativeControls = false,
    suffixIcon,
    title,
  } = props;
  const { screenSize } = useScreenSize();
  const [captionState, setCaptionState] = useState("hidden");
  const [currentValue, setCurrentValue] = useState(focusField === "end" ? dateEnd : dateStart);
  const dateStartRef = useRef();
  const InputComponent = useMemo(
    () => (simulateNativeControls ? DateInput : FormattedDate),
    [simulateNativeControls]
  );
  const outerContainer = useRef();

  useEffect(() => {
    const dateFromFocusField = focusField === "end" ? dateEnd : dateStart;

    if (simulateNativeControls) {
      const newDate = swaDate(dateFromFocusField);

      if (newDate.isValid()) {
        setCurrentValue(newDate.format(FULL_DATE_FORMAT));
      }
    } else {
      setCurrentValue(dateFromFocusField);
    }
  }, [dateStart, dateEnd]);

  useEffect(() => {
    if (simulateNativeControls && !swaDate(currentValue).isValid()) {
      setCurrentValue(swaDate().format(FULL_DATE_FORMAT));
    }
  }, [simulateNativeControls, currentValue]);

  useEffect(() => {
    onSetupEnd?.(shouldUseHorizontalLayout() ? "large" : "small");

    if (captionState !== "hidden") {
      setLocation();
    }
  }, [screenSize]);

  return (
    <Caption {...getCaptionProps()}>
      <InputComponent {...getInputFieldProps()} />
    </Caption>
  );

  function renderCalendar() {
    return (
      <div
        className={styles.dateSelector}
        onMouseDown={(event) => {
          event.stopPropagation();
          event.preventDefault();
        }}
        ref={outerContainer}
      >
        <Heading className={styles.title} styleLevel={4}>
          <div className={styles.titleContent}>{title}</div>
        </Heading>
        <Calendar {...getCalendarProps()} />
        <div className={styles.adjunct}>{adjunctContent}</div>
      </div>
    );
  }

  function renderSmallCalendar() {
    return (
      <div className={styles.dateSelector}>
        <Heading className={styles.title} styleLevel={4}>
          <div className={styles.titleContent}>{title}</div>
          <Link {...getDoneProps()}>{closeContent}</Link>
        </Heading>
        <div className={styles.adjunctSmall}>{adjunctContent}</div>
        <CalendarSecondary {...getCalendarProps()} />
      </div>
    );
  }

  function getCaptionProps() {
    const horizontalLayout = shouldUseHorizontalLayout();

    return {
      adjoiningContent: horizontalLayout ? renderCalendar() : renderSmallCalendar(),
      alignment: "center",
      bestFit: false,
      className,
      constrainFocus: false,
      inline: false,
      location: disabled ? "hidden" : captionState,
      pointerAlignment: "center",
      showPointer: horizontalLayout,
    };
  }

  function getInputFieldProps() {
    return {
      "aria-describedby": props["aria-describedby"],
      "aria-label": props["aria-label"],
      "aria-required": required,
      className: classNames({ [styles.hiddenInput]: captionState === "full-screen" }),
      disabled,
      error,
      format,
      id,
      inputMode: "none",
      name,
      onBlur: handleBlur,
      onChange: handleInputChange,
      onFocus: openOnFocus ? handleFocus : undefined,
      onKeyDown: handleKeyDown,
      onMouseDown: setLocation,
      ref,
      required,
      suffixIcon: suffixIcon || { name: "Calendar" },
      value: simulateNativeControls ? swaDate(currentValue).format(FULL_DATE_FORMAT) : currentValue,
    };
  }

  function getCalendarProps() {
    return {
      dateEnd: fillInPlaceholders(focusField === "end" ? currentValue : dateEnd),
      dateStart: fillInPlaceholders(
        focusField === "start" || !focusField ? currentValue : dateStart
      ),
      dateStartRef,
      daysOfWeekInitials,
      firstBookableDate,
      focusField,
      lastBookableDate,
      location: captionState,
      numberCalendars,
      numberDates,
      onClick: handleClick,
      ...(!shouldUseHorizontalLayout() && {
        departText,
        returnText,
        selectDateText,
      }),
    };
  }

  function getDoneProps() {
    return {
      className: styles.close,
      emphasis: false,
      onClick: () => {
        setCaptionState("hidden");
        onClose?.();
      },
      size: "fontSize12",
      styleType: "link-inline",
    };
  }

  function handleBlur(event) {
    let date = appendYear(currentValue);

    if (!isValidDate() || isEmptyDate()) {
      date = "";
    }

    if (focusField === "end") {
      if (date === "" && dateEnd === "") {
        setCurrentValue("");
      } else {
        onSelectEnd && onSelectEnd(date);
      }
    } else {
      if (date === "" && dateStart === "") {
        setCurrentValue("");
      } else {
        onSelectStart && onSelectStart(date);
      }
    }

    onChange?.({ target: { value: date } });

    if (!shouldLeaveOpen && !isElementInDateSelector(event.relatedTarget)) {
      setCaptionState("hidden");
    }
  }

  function handleClick(date, fieldWithFocus) {
    processClickSelection(date, fieldWithFocus);
  }

  function handleInputChange(event) {
    if (captionState === "hidden") {
      setLocation();
    }

    setCurrentValue(event.target.value);
  }

  function handleFocus(event) {
    if (isElementInDateSelector(event.relatedTarget)) {
      setCaptionState("hidden");
    } else {
      setLocation();
    }
  }

  function handleKeyDown(event) {
    if (captionState === "hidden" && event.key === keyCodes.KEY_ENTER) {
      event.stopPropagation();
      event.preventDefault();
      setLocation();
    } else if (event.key === keyCodes.KEY_ENTER || event.key === keyCodes.KEY_ESCAPE) {
      event.stopPropagation();
      event.preventDefault();
      processClickSelection(currentValue);
    }
  }

  function isElementInDateSelector(element) {
    return element === outerContainer?.current || outerContainer?.current?.contains(element);
  }

  function isValidDate() {
    return swaDate(appendYear(currentValue)).isValid();
  }

  function isEmptyDate() {
    return currentValue !== undefined && currentValue.replace(/\//g, "").length === 0;
  }

  function processClickSelection(currentDate, currentFocusField = focusField) {
    const dateWithYear = appendYear(currentDate);
    let isLeaveOpen;

    if (currentFocusField === "end") {
      onSelectEnd?.(dateWithYear);
    } else {
      isLeaveOpen = onSelectStart?.(dateWithYear);
    }

    if (
      (!isLeaveOpen && !shouldLeaveOpen) ||
      (currentFocusField === "end" && swaDate(currentDate).isSameOrAfter(dateStart))
    ) {
      setCaptionState("hidden");
      setCurrentValue(currentDate);
    }

    onChange?.({ target: { value: dateWithYear } });
  }

  function fillInPlaceholders(value) {
    let day = "";
    let month = "";
    let year = "";

    if (value) {
      const parts = value.split("/");

      if (format === "MM/DD/YYYY") {
        [month, day, year] = parts;

        if (month) {
          if (day.length === 0) {
            day = "01";
          }

          if (year.length < 4) {
            year = getFillInYear(month);
          }
        }
      } else {
        [month, day] = parts;

        if (month) {
          if (day.length === 0) {
            day = "01";
          }

          year = getFillInYear(month);
        }
      }
    }

    return `${month}/${day}/${year}`;
  }

  function getFillInYear(month) {
    const date = swaDate();
    const currentMonth = date.month() + 1;
    const currentYear = date.year();
    const nextYear = currentYear + 1;

    return parseInt(month) >= currentMonth ? currentYear : nextYear;
  }

  function appendYear(value) {
    let date = value;

    if (format === "MM/DD" && value) {
      const parts = value.split("/");
      const day = parts[1];
      const month = parts[0];
      let year = parts[2];

      if (!year) {
        const today = swaDate();
        const todayMonth = today.month() + 1;
        const todayYear = today.year();

        year = parseInt(month) >= todayMonth ? todayYear : todayYear + 1;
      }

      date = `${month}/${day}/${year}`;
    }

    return date;
  }

  function setLocation() {
    setCaptionState(shouldUseHorizontalLayout() ? location : "full-screen");
  }

  function shouldUseHorizontalLayout() {
    return window.innerWidth > MIN_WINDOW_WIDTH_FOR_HORIZONTAL_CALENDARS;
  }
});

DateSelector.displayName = "DateSelector";

DateSelector.propTypes = {
  /**
   * Content to display at the bottom of the calender - or the top of the calendar on small devices.
   * This content should formatted as desired by the calling component.
   */
  adjunctContent: PropTypes.node,

  /**
   * aria-describedby id to element which provides additional accessibility description of input
   * element.
   */
  "aria-describedby": PropTypes.string,

  /** aria-label text to provide additional accessibility description of input element. */
  "aria-label": PropTypes.string,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /**
   * Text to be displayed for the close option. This option is only displayed for the small/mobile
   * style.
   */
  closeContent: PropTypes.node.isRequired,

  /**
   * Date shown in FormattedDate input and that will be selected in the flyout. This date should be
   * formatted like 05/19/1982. dateEnd will be the end of a range when a start and end range is
   * needed (like for round trips).
   */
  dateEnd: PropTypes.string,

  /**
   * Date shown in FormattedDate input and that will be selected in the flyout. This date should be
   * formatted like 05/19/1982. If there is only a single date (for one-way trips), then it should
   * be given here.
   */
  dateStart: PropTypes.string,

  /** Initial letters of each day of the week: Sunday, Monday. See default value below. */
  daysOfWeekInitials: PropTypes.string,

  /** Text to display in the depart date button for mobile view. Typically: "Depart". */
  departText: PropTypes.string,

  /** Indicates FormattedDate should be disabled. */
  disabled: PropTypes.bool,

  /** Indicates Input should apply error styling and apply aria-invalid attribute. */
  error: PropTypes.bool,

  /** First date that can be booked. Should be in this format: MM/DD/YYYY. */
  firstBookableDate: PropTypes.string,

  /** Indicates which date the traveler is selecting. */
  focusField: PropTypes.oneOf(["end", "start"]),

  /** There are two supported formats that can be used. */
  format: PropTypes.oneOf(["MM/DD", "MM/DD/YYYY"]),

  /** ID to be added to input element. */
  id: PropTypes.string,

  /** Last date that can be booked. Should be in this format: MM/DD/YYYY. */
  lastBookableDate: PropTypes.string,

  /** Defines which where the calendar flyout will be displayed. */
  location: PropTypes.oneOf(["above", "below"]),

  /** Unique name in the form to represent this field. */
  name: PropTypes.string,

  /** Number of calendars displayed at one time. This value can be 1 or 2. */
  numberCalendars: PropTypes.number,

  /** One or two dates can be selected so the calendar will allow for start and end dates. */
  numberDates: PropTypes.number,

  /** Callback when mobile calendar explicitly closed. */
  onClose: PropTypes.func,

  /**
   * Event handler to learn when input the start date is given. This call will be made when
   * focusField is "end". The callback function can return true if it is desired to leave the
   * calendar open when an item is clicked.
   */
  onSelectEnd: PropTypes.func,

  /**
   * Event handler to learn when input the end date is given. This call will be made when focusField
   * is "start" or undefined. The callback function can return true if it is desired to leave the
   * calendar open when an item is clicked.
   */
  onSelectStart: PropTypes.func,

  /**
   * Callback made after calendar initially rendered. A single string parameter is returned:
   * This value will be either "small" or "large" depending on the device size.
   */
  onSetupEnd: PropTypes.func,

  /** Indicates if calendar should be opened when input is focused. */
  openOnFocus: PropTypes.bool,

  /** Indicates Input should apply aria-required attribute. */
  required: PropTypes.bool,

  /** Text to display in the return date button for mobile view. Typically: "RETURN". */
  returnText: PropTypes.string,

  /** Text to display when no date is selected in mobile view. */
  selectDateText: PropTypes.string,

  /**
   * Leave mobile version open when a selection is made. Caption must be explicitly closed via close
   * button.
   */
  shouldLeaveOpen: PropTypes.bool,

  /** Indicates if the component should use DateInput to simulate native HTML date input. */
  simulateNativeControls: PropTypes.bool,

  /** Icon that can be placed on right side of Input. See Icon for more details. */
  suffixIcon: inputPropTypes.suffixIcon,

  /** Heading to appear at top of flyout. */
  title: PropTypes.string.isRequired,
};
